import { fetchAccessToken } from "../frame";
import {
  type AccessTokenResponse,
  type AccessTokenFunction,
  type OwletEventHandlers,
} from "../types";
import type { IWACPageCommand } from "./messageTypes/Command";
import type {
  IAcknowledgeMessage,
  IMessage,
  IResultMessage,
} from "./messageTypes/Message";
import type { IWACPageNotification } from "./messageTypes/Notification";
import { messagePort, sendMessage } from "./channelMessageUtility";
import type { IWACPageAuthenticateResult } from "./messageTypes/Result";
import type { AdaptiveLogger } from "@1js/office-online-jsapi-core";
import { LogCategory, TraceLevel } from "@1js/office-online-jsapi-core";
import { endSessionFromHost } from "../bootApplicationFromUiHost";

// When doc.aspx sends a command, they expect a notification that
// the command has been received
function sendAck() {
  messagePort && sendMessage({ type: "acknowledge" } as IAcknowledgeMessage);
}

export async function handleMessage(
  message: IMessage,
  eventHandlers: OwletEventHandlers,
  getAuthToken: AccessTokenFunction,
  logger?: AdaptiveLogger
): Promise<void> {
  switch (message.type) {
    case "command":
      const WACPageCommand = message.data as IWACPageCommand;
      sendAck();
      switch (WACPageCommand.command) {
        case "authenticate":
          logger?.ULS.sendTraceTag(
            0x1e355463 /* tag_4nvr9 */,
            LogCategory.msoulscat_Wac_UnifiedUiHost,
            TraceLevel.Important,
            `AuthRenewRequest: Refetch access token ${WACPageCommand.claimsChallenge?.claims ? "with" : "without"} claims`
          );
          const tokenResponse: AccessTokenResponse = await fetchAccessToken(
            getAuthToken,
            WACPageCommand.resource,
            WACPageCommand.claimsChallenge?.claims
              ? [WACPageCommand.claimsChallenge.claims]
              : undefined
          );
          const tokenResult: IResultMessage = {
            type: "result",
            id: message.id,
            data: {
              result: "token",
              token: tokenResponse.token,
            } as IWACPageAuthenticateResult,
          };
          sendMessage(tokenResult);
          break;
        default:
          break;
      }
      break;
    case "notification":
      const WACPageNotification = message.data as IWACPageNotification;
      switch (WACPageNotification.notification) {
        case "page-loaded":
          // result could be a failure, but we still want to invoke the handler in that case
          if (WACPageNotification.result) {
            eventHandlers.onPageLoaded(
              WACPageNotification.hostGeo || "",
              WACPageNotification.result
            );
          } else {
            eventHandlers.onBootFailure && eventHandlers.onBootFailure();
          }
          break;
        case "session-ended":
          await endSessionFromHost();
          eventHandlers.onDocumentClose?.();
          break;
        case "app-accessibility-loop-complete":
          eventHandlers.onReturnFocus?.(WACPageNotification.navigateForward);
          break;
        case "init-diagnostics":
          // Do something with appInitDiagnostics
          break;
        case "doc-renamed":
          eventHandlers.onRename?.(WACPageNotification.newName);
          break;
        case "doc-reboot":
          eventHandlers.onRebootDocument(
            WACPageNotification.documentUrl,
            eventHandlers,
            WACPageNotification.reason,
            WACPageNotification.mode
          );
          break;
        case "create-new": // This can reuse reboot code, since the doc is created before calling doc.aspx
          eventHandlers.onRebootDocument(
            WACPageNotification.documentUrl,
            eventHandlers,
            "createNew"
          );
          break;
        case "boot-failed":
          eventHandlers.onBootFailure?.();
          break;
        default:
          // Handle other notification types
          break;
      }
      break;
    default:
      // Handle other message types
      break;
  }
}
